import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./lib/auth";
import Shell from "./components/Shell";
import Login from "./pages/Login";
import RunMyDay from "./pages/RunMyDay";
import LeadsList from "./pages/LeadsList";
import AddLead from "./pages/AddLead";
import LeadDetail from "./pages/LeadDetail";
import ImportLeads from "./pages/ImportLeads";
// Dashboard page merged into RunMyDay — stats now live on `/`.
import Transactions from "./pages/Transactions";
import TransactionDetail from "./pages/TransactionDetail";
import Tasks from "./pages/Tasks";
import Documents from "./pages/Documents";
import Templates from "./pages/Templates";
import Clients from "./pages/Clients";
import ClientDetail from "./pages/ClientDetail";
import Messages from "./pages/Messages";
import Settings from "./pages/Settings";
import Onboarding from "./pages/Onboarding";
import Referrals from "./pages/Referrals";
import Reset from "./pages/Reset";
import ResetConfirm from "./pages/ResetConfirm";
import NotFound from "./pages/NotFound";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import ErrorBoundary from "./components/ErrorBoundary";
import PortalVerify from "./pages/portal/PortalVerify";
import PortalHome from "./pages/portal/PortalHome";
import PortalTransaction from "./pages/portal/PortalTransaction";
import PortalChat from "./pages/portal/PortalChat";
import PortalExpired from "./pages/portal/PortalExpired";
import PortalSignComplete from "./pages/portal/PortalSignComplete";
import JoinReferral from "./pages/JoinReferral";
import Properties from "./pages/Properties";
import Listings from "./pages/Listings";import PublicShare from "./pages/PublicShare";
import Community from "./pages/Community";
import AdminDashboard from "./pages/AdminDashboard";
import PreviewFelecia from "./pages/PreviewFelecia";
import GA4PageViews from "./components/GA4PageViews";
import Landing from "./pages/Landing";
import About from "./pages/About";
import Pricing from "./pages/Pricing";
import BestCrmForSoloAgents from "./pages/BestCrmForSoloAgents";
import VsIndex from "./pages/VsIndex";
import VsFollowUpBoss from "./pages/VsFollowUpBoss";
import VsWiseAgent from "./pages/VsWiseAgent";
import VsTopProducer from "./pages/VsTopProducer";
import VsKvcore from "./pages/VsKvcore";

function Protected({ children }) {
  const { agent, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center text-slate-500 text-sm">Loading…</div>;
  // Unauthenticated visitors get bounced to the auth card, not the marketing
  // landing — landing lives at `/` and would leave them looping.
  if (!agent) return <Navigate to="/auth" replace />;
  return <Shell>{children}</Shell>;
}

/**
 * Root-route resolver:
 *  - Signed-in agent  → the app shell (RunMyDay dashboard)
 *  - Public-preview token holder → same app shell (renders as the
 *    preview target agent via the axios interceptor)
 *  - Everyone else → the public marketing landing page.
 * This lets ikey-app.com/ double as both the marketing site and the
 * app entry without a redirect flicker.
 */
function RootRoute() {
  const { agent, loading } = useAuth();
  if (loading) return <div className="min-h-screen grid place-items-center text-slate-500 text-sm">Loading…</div>;
  // Preview flow: PreviewFelecia stashes a token under IMPERSONATION_KEY
  // in sessionStorage and then routes to `/`. Without this check the new
  // Landing page would swallow the preview visitor.
  let hasPreviewToken = false;
  try {
    const tok = sessionStorage.getItem("ikey_impersonation_token");
    hasPreviewToken = !!(tok && tok.startsWith("iKP."));
  } catch (_) { /* sessionStorage disabled — fall through */ }
  if (!agent && !hasPreviewToken) return <Landing />;
  return <Shell><RunMyDay /></Shell>;
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <GA4PageViews />
          <Toaster richColors position="top-right" />
          <Routes>
            {/* /auth is the new home for the sign-up/sign-in card.
                /login stays as a permanent redirect so bookmarks,
                password-reset emails, and every external link that ever
                referenced the old URL keep working. */}
            <Route path="/auth" element={<Login />} />
            <Route path="/login" element={<Navigate to="/auth" replace />} />
            <Route path="/reset" element={<Reset />} />
            <Route path="/reset/confirm" element={<ResetConfirm />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/about" element={<About />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/best-crm-for-solo-agents" element={<BestCrmForSoloAgents />} />
            <Route path="/vs" element={<VsIndex />} />
            <Route path="/vs/follow-up-boss" element={<VsFollowUpBoss />} />
            <Route path="/vs/wise-agent" element={<VsWiseAgent />} />
            <Route path="/vs/top-producer" element={<VsTopProducer />} />
            <Route path="/vs/kvcore" element={<VsKvcore />} />
            <Route path="/join/:code" element={<JoinReferral />} />
            <Route path="/share/:token" element={<PublicShare />} />
            {/* One-off 48h public marketing preview. Unauthenticated —
                mints a read-only preview JWT, stashes it, and redirects
                to the full agent app rendering as Felecia Monroe. */}
            <Route path="/preview/felecia-demo" element={<PreviewFelecia />} />
            {/* Root = marketing landing for guests, dashboard for agents. */}
            <Route path="/" element={<RootRoute />} />
            <Route path="/dashboard" element={<Navigate to="/" replace />} />
            <Route path="/leads" element={<Protected><LeadsList /></Protected>} />
            <Route path="/leads/new" element={<Protected><AddLead /></Protected>} />
            <Route path="/leads/import" element={<Protected><ImportLeads /></Protected>} />
            <Route path="/clients/import" element={<Protected><ImportLeads /></Protected>} />
            <Route path="/leads/:id" element={<Protected><LeadDetail /></Protected>} />
            <Route path="/properties" element={<Protected><Properties /></Protected>} />
            <Route path="/listings" element={<Protected><Listings /></Protected>} />
            <Route path="/mls" element={<Navigate to="/listings" replace />} />
            <Route path="/transactions" element={<Protected><Transactions /></Protected>} />
            <Route path="/transactions/:id" element={<Protected><TransactionDetail /></Protected>} />
            <Route path="/tasks" element={<Protected><Tasks /></Protected>} />
            <Route path="/documents" element={<Protected><Documents /></Protected>} />
            <Route path="/templates" element={<Protected><Templates /></Protected>} />
            <Route path="/clients" element={<Protected><Clients /></Protected>} />
            <Route path="/clients/:id" element={<Protected><ClientDetail /></Protected>} />
            <Route path="/messages" element={<Protected><Messages /></Protected>} />
            <Route path="/settings" element={<Protected><Settings /></Protected>} />
            <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />
            <Route path="/referrals" element={<Protected><Referrals /></Protected>} />
            <Route path="/community" element={<Protected><Community /></Protected>} />
            <Route path="/admin" element={<Protected><AdminDashboard /></Protected>} />
            <Route path="/admin/feedback" element={<Protected><AdminDashboard initialTab="feedback" /></Protected>} />
            {/* Team management merged into /settings (single Save button covers both).
                Redirect any old links or bookmarks so nobody lands on an empty page. */}
            <Route path="/brokerage" element={<Navigate to="/settings" replace />} />

            {/* Client portal — separate tree, magic-link auth, agent's brand */}
            <Route path="/portal/verify" element={<PortalVerify />} />
            <Route path="/portal/expired" element={<PortalExpired />} />
            <Route path="/portal/sign-complete" element={<PortalSignComplete />} />
            <Route path="/portal" element={<PortalHome />} />
            <Route path="/portal/messages" element={<PortalChat />} />
            <Route path="/portal/transactions/:id" element={<PortalTransaction />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
